import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { isNativeApp } from "@/lib/platform";

/**
 * Native-feeling pull-to-refresh gesture.
 *
 * Enabled only inside the Capacitor app (skipped on the web). Listens for a
 * downward drag starting when the window is scrolled to the very top and,
 * once the user has pulled past ~70 px, calls `onRefresh()`. A small chip
 * with a spinning icon animates from the top edge to give visual feedback.
 *
 * ⚠️ The touch listeners must be attached ONCE for the lifetime of the
 * component. Attaching them inside an effect that depends on `pull` /
 * `refreshing` (i.e. re-attaching on every setState) breaks scroll on
 * Android WebView — the browser interprets the constant listener churn as
 * "these handlers are eating my scroll" and stops firing pan gestures.
 */
const THRESHOLD = 70;      // px to pull before triggering
const MAX_PULL = 110;      // clamp so the indicator stops growing
const RESIST = 0.5;        // drag resistance factor (feels iOS-ish)

export default function PullToRefresh({ onRefresh, children }) {
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const enabled = isNativeApp();

    // Refs mirror the latest values so the (stable) event handlers can read
    // them without needing to be re-created on every render.
    const startY = useRef(null);
    const pullRef = useRef(0);
    const refreshingRef = useRef(false);
    const onRefreshRef = useRef(onRefresh);

    useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);
    useEffect(() => { pullRef.current = pull; }, [pull]);
    useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);

    useEffect(() => {
        if (!enabled) return;

        const onTouchStart = (e) => {
            if (refreshingRef.current) return;
            // Only arm the gesture when the page is already at the top — this
            // prevents us from stealing regular scrolling in the middle of the page.
            if (window.scrollY > 0) { startY.current = null; return; }
            startY.current = e.touches[0].clientY;
        };

        const onTouchMove = (e) => {
            if (startY.current == null) return;
            const dy = e.touches[0].clientY - startY.current;
            if (dy <= 0) {
                if (pullRef.current !== 0) setPull(0);
                return;
            }
            const eased = Math.min(MAX_PULL, dy * RESIST);
            // Avoid pointless renders when the eased value barely changes.
            if (Math.abs(eased - pullRef.current) < 1) return;
            setPull(eased);
        };

        const onTouchEnd = async () => {
            if (startY.current == null) return;
            startY.current = null;
            if (pullRef.current >= THRESHOLD && onRefreshRef.current) {
                setRefreshing(true);
                try { await onRefreshRef.current(); } catch { /* swallow */ }
                setRefreshing(false);
            }
            setPull(0);
        };

        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        window.addEventListener("touchend", onTouchEnd, { passive: true });
        window.addEventListener("touchcancel", onTouchEnd, { passive: true });
        return () => {
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
            window.removeEventListener("touchcancel", onTouchEnd);
        };
        // enabled never changes at runtime, so this effect runs exactly once.
    }, [enabled]);

    if (!enabled) return <>{children}</>;

    const opacity = Math.min(1, pull / THRESHOLD);
    const rotate = (pull / THRESHOLD) * 180;
    const active = startY.current != null;

    return (
        <>
            <div
                aria-hidden={pull === 0 && !refreshing}
                data-testid="pull-to-refresh-indicator"
                className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-none"
                style={{
                    top: `calc(env(safe-area-inset-top) + 12px)`,
                    transform: `translate(-50%, ${refreshing ? 0 : pull - THRESHOLD}px)`,
                    opacity: refreshing ? 1 : opacity,
                    transition: active ? "none" : "transform 220ms ease-out, opacity 220ms ease-out",
                }}
            >
                <div className="w-10 h-10 rounded-full bg-white shadow-lg border border-border/60 flex items-center justify-center">
                    <RefreshCw
                        className={`w-5 h-5 text-primary ${refreshing ? "animate-spin" : ""}`}
                        style={refreshing ? undefined : { transform: `rotate(${rotate}deg)`, transition: "transform 60ms linear" }}
                    />
                </div>
            </div>
            {children}
        </>
    );
}
